/**
 * Scope state — which portfolio the user is currently viewing.
 *
 * Values:
 *   'own'             → user's own portfolio (default)
 *   '<user_uuid>'     → peer's portfolio (requires portfolio:view_others)
 *   '<email>'         → legacy peer scope (still accepted by backend)
 *   'all'             → entire fleet (requires portfolio:view_all)
 *
 * The active value is sent to every authenticated request as the
 * `X-AMOS-Scope` HTTP header by ApiClient.addAuthHeaders. Persisted in
 * sessionStorage so the choice survives a page refresh — but stays per-tab,
 * so an admin can debug Calvin's portfolio in one tab and Chana's in another.
 *
 * URL is no longer the source of truth. Old-style ?scope=… deep links are
 * consumed once on cold load (`hydrateScope`) and `replaceState` rewrites
 * the URL to the canonical clean form.
 */

const STORAGE_KEY = 'amos:scope';

interface ScopeState {
	active: string;
	picker: { open: boolean };
}

export const scopeState = $state<ScopeState>({
	active: 'own',
	picker: { open: false }
});

/**
 * Update the active scope and persist to sessionStorage. The next request
 * fired by ApiClient will carry the new `X-AMOS-Scope` header.
 *
 * 'own' is the default, so we *delete* the key rather than store it — keeps
 * sessionStorage clean and matches the backend's "no header → own" path.
 *
 * Scope v2 Phase 5 — also rotates the WebSocket subscription so this tab
 * receives precise per-scope home:invalidate broadcasts for the new scope
 * (and stops receiving them for the old one).
 */
export function setScope(value: string): void {
	const previous = scopeState.active;
	scopeState.active = value;
	if (typeof window === 'undefined') return;
	if (value === 'own') {
		sessionStorage.removeItem(STORAGE_KEY);
	} else {
		sessionStorage.setItem(STORAGE_KEY, value);
	}
	rotateScopeSubscription(previous, value);
}

/**
 * Send WS SCOPE_CHANGE so the BE can re-run permission-checked scope
 * resolution and swap the connection's asset_id filter — without dropping
 * the socket. The BE's _handle_scope_change handler does the topic
 * subscription rotation atomically alongside the metadata swap.
 *
 * We KEEP an UNSUBSCRIBE for the previous `home:scope:{old}` topic as a
 * defense-in-depth pre-emptive prune (matches the prior Phase 5 behavior).
 * The BE rotation in _handle_scope_change is idempotent so the duplicate
 * unsubscribe is safe.
 *
 * Best-effort — never throws (WS may be disconnected). Dynamic imports
 * avoid a circular dep with the websocket actions module.
 */
function rotateScopeSubscription(previous: string, next: string): void {
	if (previous === next) return;
	void (async () => {
		try {
			const { sendMessage } = await import(
				'$lib/services/websocket/websocket.actions.svelte'
			);
			const { MessageType } = await import('$lib/types/websocket/messages.types');
			// Re-resolve scope server-side (re-runs the permission check + swaps
			// asset_id filter + rotates `home:scope:*` topic subscription).
			sendMessage(MessageType.SCOPE_CHANGE, {
				scope_id: next || 'own'
			});
			// Belt-and-suspenders: pre-emptively unsubscribe from old invalidation
			// topic in case the BE handler errors out — better to miss invalidations
			// for the old scope than to receive them on the new one.
			if (previous) {
				sendMessage(MessageType.UNSUBSCRIBE, {
					topics: [`home:scope:${previous}`]
				});
			}
		} catch {
			// WS not initialized yet; the connect-time ?scope_id= URL param
			// will carry the active scope when the socket comes online.
		}
	})();
}

/**
 * Initialize active scope on cold load.
 *
 * Priority:
 *   1. URL `?scope=…` (legacy deep link) — consumed once, then `replaceState`
 *      rewrites the URL to the clean form so the link doesn't keep leaking.
 *   2. sessionStorage `amos:scope` — what we set last time in this tab.
 *   3. Default 'own'.
 */
export function hydrateScope(): void {
	if (typeof window === 'undefined') return;

	const url = new URL(window.location.href);
	const param = url.searchParams.get('scope');
	if (param) {
		const value = decodeURIComponent(param);
		scopeState.active = value;
		if (value === 'own') {
			sessionStorage.removeItem(STORAGE_KEY);
		} else {
			sessionStorage.setItem(STORAGE_KEY, value);
		}
		url.searchParams.delete('scope');
		window.history.replaceState({}, '', url.toString());
		return;
	}

	const stored = sessionStorage.getItem(STORAGE_KEY);
	scopeState.active = stored ?? 'own';
}

/**
 * Backward-compatible alias for the previous `hydrateScopeFromUrl` name.
 * Prefer `hydrateScope` in new code.
 */
export const hydrateScopeFromUrl = hydrateScope;

export function resetScope(): void {
	scopeState.active = 'own';
	scopeState.picker.open = false;
	if (typeof window !== 'undefined') {
		sessionStorage.removeItem(STORAGE_KEY);
	}
}

// ── Cross-user leak prevention (defense in depth) ────────────────────────
// AuthService.clearSession() is the PRIMARY path and calls purgeUserData()
// which wipes sessionStorage entirely. This listener exists in case a
// different auth path emits auth:logout / auth:login-success without
// going through clearSession (e.g., cross-tab BroadcastChannel logout,
// token-expiry teardown, recovery-state forced reauth).
//
// We call resetScope() here rather than purgeUserData() because
// purgeUserData would clear far more than this module owns — leaving
// that to AuthService keeps responsibilities clean. resetScope() does
// the minimum: nuke the scope-specific key + reset in-memory state.
//
// Dynamic import so this module stays loadable in SSR/test environments
// where eventBus isn't available; failures are silent.
if (typeof window !== 'undefined') {
	void (async () => {
		try {
			const { eventBus } = await import('$lib/core/services/EventBusService');
			eventBus.on('auth:logout', () => resetScope());
			// Also reset on login. Secure default: a fresh login never
			// inherits a prior tab's scope from sessionStorage, even if
			// AuthService.clearSession() wasn't called between sessions
			// (e.g., cross-tab login via BroadcastChannel).
			eventBus.on('auth:login-success', () => resetScope());
		} catch {
			/* eventBus unavailable; AuthService.clearSession is primary path */
		}
	})();
}
